/**
 * ActionsByHypothesisPanel - 按假设（风险项）分组的建议动作面板
 * 
 * 设计说明：
 * - 按风险项分组，每个风险项独立卡片
 * - 每个卡片可展开/收起
 * - 内部按 A/B/C 三级分区展示动作
 * - 致命缺口优先展示
 */
import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Copy,
  Check,
  X,
  Clock,
  Zap,
  Target,
  Shield,
  Loader2,
  Eye,
  EyeOff,
  Link2,
  FileText
} from 'lucide-react'
import { clsx } from 'clsx'
import type { HypothesisActions, ActionGrade, Action, ArtifactType } from '@/types'
import { ARTIFACT_TYPE_NAMES } from '@/types'
import ArtifactQuickGenModal from './ArtifactQuickGenModal'
import ActionAcceptModal from './ActionAcceptModal'
import SignalConfirmModal from './SignalConfirmModal'
import ActionRejectModal from './ActionRejectModal'

// TASK-H8: 证据选项接口
export interface EvidenceOption {
  id: string
  summary: string
  sourceType?: string
  occurredAt?: string
}

// 待确认信号接口（完成动作后返回）
interface PendingSignal {
  id: string
  strength: 'strong' | 'medium' | 'weak'
  description: string
  requiresConfirmation: boolean
}

// 动作完成响应接口
interface CompleteActionResponse {
  actionId: string
  pendingSignals?: PendingSignal[]
  requiresSignalConfirmation?: boolean
  message?: string
}

interface ActionsByHypothesisPanelProps {
  hypotheses: HypothesisActions[]
  totalTimeCostDays: number
  recommendedGrade: ActionGrade
  isLoading?: boolean
  // 商机信息（用于输出件生成弹窗）
  dealId?: string
  dealName?: string
  // 升级版回调：返回 Promise 以支持弹窗闭环
  onAcceptAction?: (actionId: string, scheduledDate?: string) => Promise<void>
  onCompleteAction?: (actionId: string) => Promise<CompleteActionResponse>
  onRejectAction?: (actionId: string, reason?: string) => Promise<void>
  onMarkNotApplicable?: (actionId: string) => Promise<void>
  onCopyAction?: (content: string) => void
  // 刷新回调（状态变更后刷新数据）
  onRefresh?: () => void
  // 显示 Toast 回调
  onShowToast?: (message: string, type?: 'success' | 'error' | 'info') => void
  // TASK-H8: 信号追踪原位展开支持
  onObserveSignal?: (signalId: string, evidenceId?: string, notes?: string) => Promise<{
    hypothesisUpdated?: boolean
    hypothesisUpdate?: {
      hypothesisId: string
      oldStatus: string
      newStatus: string
    }
  }>
  onUnobserveSignal?: (signalId: string) => Promise<void>
  onFetchEvidences?: (hypothesisId: string) => Promise<EvidenceOption[]>
  getEvidenceSummary?: (evidenceId: string) => string | undefined
}

// 等级配置
const GRADE_CONFIG: Record<ActionGrade, {
  label: string
  description: string
  color: string
  bgColor: string
  borderColor: string
}> = {
  A: {
    label: '最佳路径',
    description: '完整验证，深度闭环',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
  },
  B: {
    label: '快速验证',
    description: '关键信号快速验证',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  C: {
    label: '止血动作',
    description: '不验证，只防输',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
}

// 动作状态配置
const ACTION_STATUS_CONFIG: Record<string, {
  label: string
  color: string
  bgColor: string
  borderColor: string
}> = {
  proposed: {
    label: '待处理',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-200',
  },
  accepted: {
    label: '已采纳',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-200',
  },
  done: {
    label: '已完成',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-200',
  },
  rejected: {
    label: '已拒绝',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-200',
  },
  not_applicable: {
    label: '不适用',
    color: 'text-gray-500',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-200',
  },
}

const isArtifactType = (value: unknown): value is ArtifactType =>
  typeof value === 'string' && value in ARTIFACT_TYPE_NAMES

export default function ActionsByHypothesisPanel({
  hypotheses,
  totalTimeCostDays,
  recommendedGrade: _recommendedGrade,
  isLoading = false,
  dealId,
  dealName,
  onAcceptAction,
  onCompleteAction,
  onRejectAction,
  onMarkNotApplicable,
  onCopyAction,
  onRefresh,
  onShowToast,
  onObserveSignal,
  onUnobserveSignal,
  onFetchEvidences,
  getEvidenceSummary: _getEvidenceSummary,
}: ActionsByHypothesisPanelProps) {
  // 展开状态：默认展开第一个致命缺口
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    const firstFatalGap = hypotheses.find(h => h.isFatalGap)
    return new Set(firstFatalGap ? [firstFatalGap.hypothesisId] : hypotheses.slice(0, 1).map(h => h.hypothesisId))
  })
  
  const [copiedId, setCopiedId] = useState<string | null>(null)
  
  // 输出件生成弹窗状态
  const [artifactModal, setArtifactModal] = useState<{
    isOpen: boolean
    artifactType: ArtifactType | null
    actionContent: string
  }>({ isOpen: false, artifactType: null, actionContent: '' })
  
  // ============================================
  // 闭环弹窗状态
  // ============================================
  
  // 采纳弹窗状态
  const [acceptModal, setAcceptModal] = useState<{
    isOpen: boolean
    actionId: string
    actionContent: string
    actionGrade: 'A' | 'B' | 'C'
    timeCostDisplay?: string
  }>({ isOpen: false, actionId: '', actionContent: '', actionGrade: 'B' })
  
  // 信号确认弹窗状态
  const [signalModal, setSignalModal] = useState<{
    isOpen: boolean
    actionContent: string
    pendingSignals: PendingSignal[]
  }>({ isOpen: false, actionContent: '', pendingSignals: [] })
  
  // 拒绝弹窗状态
  const [rejectModal, setRejectModal] = useState<{
    isOpen: boolean
    actionId: string
    actionContent: string
    actionGrade: 'A' | 'B' | 'C'
  }>({ isOpen: false, actionId: '', actionContent: '', actionGrade: 'B' })
  
  // 加载状态
  const [actionLoadingIds, setActionLoadingIds] = useState<Set<string>>(new Set())
  
  // TASK-H8: 信号追踪展开状态
  const [expandedActionIds, setExpandedActionIds] = useState<Set<string>>(new Set())
  const [expandedSignalIds, setExpandedSignalIds] = useState<Set<string>>(new Set())
  const [signalNotes, setSignalNotes] = useState<Record<string, string>>({})
  const [selectedEvidences, setSelectedEvidences] = useState<Record<string, string>>({})
  const [loadingSignals, setLoadingSignals] = useState<Set<string>>(new Set())
  const [evidenceCache, setEvidenceCache] = useState<Record<string, EvidenceOption[]>>({})
  const [loadingEvidences, setLoadingEvidences] = useState<Set<string>>(new Set())

  const toggleExpand = useCallback((hypothesisId: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(hypothesisId)) {
        next.delete(hypothesisId)
      } else {
        next.add(hypothesisId)
      }
      return next
    })
  }, [])
  
  // ============================================
  // 闭环操作处理函数
  // ============================================
  
  // 打开采纳弹窗
  const handleOpenAcceptModal = useCallback((action: Action) => {
    setAcceptModal({
      isOpen: true,
      actionId: action.id!,
      actionContent: action.content,
      actionGrade: action.actionGrade as 'A' | 'B' | 'C',
      timeCostDisplay: action.timeCostDisplay
    })
  }, [])
  
  // 确认采纳动作
  const handleConfirmAccept = useCallback(async (scheduledDate?: string) => {
    if (!onAcceptAction) return
    await onAcceptAction(acceptModal.actionId, scheduledDate)
    onShowToast?.('动作已采纳，已加入待办事项', 'success')
    onRefresh?.()
  }, [acceptModal.actionId, onAcceptAction, onShowToast, onRefresh])
  
  // 打开拒绝弹窗
  const handleOpenRejectModal = useCallback((action: Action) => {
    setRejectModal({
      isOpen: true,
      actionId: action.id!,
      actionContent: action.content,
      actionGrade: action.actionGrade as 'A' | 'B' | 'C'
    })
  }, [])
  
  // 确认拒绝动作
  const handleConfirmReject = useCallback(async (reason?: string) => {
    if (!onRejectAction) return
    await onRejectAction(rejectModal.actionId, reason)
    onShowToast?.('动作已拒绝', 'info')
    onRefresh?.()
  }, [rejectModal.actionId, onRejectAction, onShowToast, onRefresh])
  
  // 完成动作（可能触发信号确认弹窗）
  const handleCompleteAction = useCallback(async (action: Action) => {
    if (!onCompleteAction) return
    
    setActionLoadingIds(prev => new Set(prev).add(action.id!))
    
    try {
      const response = await onCompleteAction(action.id!)
      
      // 如果有待确认信号，打开信号确认弹窗
      if (response.requiresSignalConfirmation && response.pendingSignals?.length) {
        setSignalModal({
          isOpen: true,
          actionContent: action.content,
          pendingSignals: response.pendingSignals
        })
        onShowToast?.(`动作已完成，有 ${response.pendingSignals.length} 个信号待确认`, 'info')
      } else {
        onShowToast?.('动作已完成', 'success')
        onRefresh?.()
      }
    } catch (err) {
      onShowToast?.(err instanceof Error ? err.message : '操作失败', 'error')
    } finally {
      setActionLoadingIds(prev => {
        const next = new Set(prev)
        next.delete(action.id!)
        return next
      })
    }
  }, [onCompleteAction, onShowToast, onRefresh])
  
  // 标记不适用
  const handleMarkNotApplicable = useCallback(async (actionId: string) => {
    if (!onMarkNotApplicable) return
    
    setActionLoadingIds(prev => new Set(prev).add(actionId))
    
    try {
      await onMarkNotApplicable(actionId)
      onShowToast?.('已标记为不适用', 'info')
      onRefresh?.()
    } catch (err) {
      onShowToast?.(err instanceof Error ? err.message : '操作失败', 'error')
    } finally {
      setActionLoadingIds(prev => {
        const next = new Set(prev)
        next.delete(actionId)
        return next
      })
    }
  }, [onMarkNotApplicable, onShowToast, onRefresh])
  
  // 信号确认弹窗中的信号观测
  const handleSignalModalObserve = useCallback(async (signalId: string, notes?: string) => {
    if (!onObserveSignal) {
      return { hypothesisUpdated: false }
    }
    return await onObserveSignal(signalId, undefined, notes)
  }, [onObserveSignal])
  
  // 关闭信号确认弹窗
  const handleCloseSignalModal = useCallback(() => {
    setSignalModal({ isOpen: false, actionContent: '', pendingSignals: [] })
    onRefresh?.()
  }, [onRefresh])

  const handleCopy = useCallback(async (action: Action | { id: string; content: string }) => {
    try {
      await navigator.clipboard.writeText(action.content)
      setCopiedId(action.id)
      onCopyAction?.(action.content)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [onCopyAction])
  
  // TASK-H8: 展开/收起动作的信号追踪
  const toggleActionExpand = useCallback((actionId: string) => {
    setExpandedActionIds(prev => {
      const next = new Set(prev)
      if (next.has(actionId)) {
        next.delete(actionId)
      } else {
        next.add(actionId)
      }
      return next
    })
  }, [])
  
  // TASK-H8: 展开/收起单个信号
  const toggleSignalExpand = useCallback((signalId: string) => {
    setExpandedSignalIds(prev => {
      const next = new Set(prev)
      if (next.has(signalId)) {
        next.delete(signalId)
      } else {
        next.add(signalId)
      }
      return next
    })
  }, [])
  
  // TASK-H8: 获取假设相关证据
  const fetchEvidencesForHypothesis = useCallback(async (hypothesisId: string) => {
    if (evidenceCache[hypothesisId] || loadingEvidences.has(hypothesisId) || !onFetchEvidences) return
    
    setLoadingEvidences(prev => new Set(prev).add(hypothesisId))
    try {
      const evidences = await onFetchEvidences(hypothesisId)
      setEvidenceCache(prev => ({ ...prev, [hypothesisId]: evidences }))
    } catch (err) {
      console.error('Failed to fetch evidences:', err)
    } finally {
      setLoadingEvidences(prev => {
        const next = new Set(prev)
        next.delete(hypothesisId)
        return next
      })
    }
  }, [onFetchEvidences, evidenceCache, loadingEvidences])
  
  // TASK-H8: 标记信号为已观测
  const handleObserveSignal = useCallback(async (signalId: string) => {
    if (!onObserveSignal) return
    
    setLoadingSignals(prev => new Set(prev).add(signalId))
    try {
      await onObserveSignal(
        signalId, 
        selectedEvidences[signalId] || undefined,
        signalNotes[signalId] || undefined
      )
      // 清理状态
      setExpandedSignalIds(prev => {
        const next = new Set(prev)
        next.delete(signalId)
        return next
      })
      setSignalNotes(prev => {
        const next = { ...prev }
        delete next[signalId]
        return next
      })
      setSelectedEvidences(prev => {
        const next = { ...prev }
        delete next[signalId]
        return next
      })
    } catch (err) {
      console.error('Failed to observe signal:', err)
    } finally {
      setLoadingSignals(prev => {
        const next = new Set(prev)
        next.delete(signalId)
        return next
      })
    }
  }, [onObserveSignal, selectedEvidences, signalNotes])
  
  // TASK-H8: 取消观测标记
  const handleUnobserveSignal = useCallback(async (signalId: string) => {
    if (!onUnobserveSignal) return
    
    setLoadingSignals(prev => new Set(prev).add(signalId))
    try {
      await onUnobserveSignal(signalId)
    } catch (err) {
      console.error('Failed to unobserve signal:', err)
    } finally {
      setLoadingSignals(prev => {
        const next = new Set(prev)
        next.delete(signalId)
        return next
      })
    }
  }, [onUnobserveSignal])

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
        <Loader2 className="w-8 h-8 mx-auto mb-4 text-primary-600 animate-spin" />
        <p className="text-gray-500">正在加载建议动作...</p>
      </div>
    )
  }

  if (hypotheses.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
        <Zap className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">暂无建议动作</h3>
        <p className="text-gray-500">添加证据并完成诊断后，系统将自动生成针对性的建议动作。</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* 头部 */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xl">📋</span>
          <h3 className="font-semibold text-gray-900">建议动作</h3>
          <span className="text-sm text-gray-500">
            共 {hypotheses.length} 个风险项
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Clock className="w-4 h-4" />
          <span>预计总耗时: {totalTimeCostDays.toFixed(1)} 天</span>
        </div>
      </div>

      {/* 风险项列表 */}
      <div className="divide-y divide-gray-100">
        {hypotheses.map((hypothesis) => {
          const isExpanded = expandedIds.has(hypothesis.hypothesisId)
          
          return (
            <div key={hypothesis.hypothesisId} className="bg-white">
              {/* 风险项头部 */}
              <button
                onClick={() => toggleExpand(hypothesis.hypothesisId)}
                className={clsx(
                  'w-full px-6 py-4 flex items-center justify-between transition-colors',
                  'hover:bg-gray-50',
                  hypothesis.isFatalGap && 'bg-red-50/50'
                )}
              >
                <div className="flex items-center gap-3">
                  {hypothesis.isFatalGap ? (
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  ) : (
                    <Target className="w-5 h-5 text-gray-400" />
                  )}
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900">
                      {hypothesis.hypothesisTitle}
                    </h4>
                    <div className="flex items-center gap-2 mt-0.5">
                      {hypothesis.isFatalGap && (
                        <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                          致命缺口
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        {hypothesis.category}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={clsx(
                    'text-xs px-2 py-1 rounded-full font-medium',
                    GRADE_CONFIG[hypothesis.recommendedGrade].bgColor,
                    GRADE_CONFIG[hypothesis.recommendedGrade].color
                  )}>
                    推荐 {hypothesis.recommendedGrade} 级
                  </span>
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* 展开内容 */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-4 space-y-4">
                      {/* A/B/C 三级动作 */}
                      {(['A', 'B', 'C'] as ActionGrade[]).map((grade) => {
                        const gradeData = hypothesis.actionsByGrade[grade]
                        const config = GRADE_CONFIG[grade]
                        const isRecommended = grade === hypothesis.recommendedGrade
                        const actions = gradeData?.actions || []
                        
                        if (actions.length === 0) return null
                        
                        return (
                          <div
                            key={grade}
                            className={clsx(
                              'rounded-xl border-2 overflow-hidden',
                              isRecommended ? config.borderColor : 'border-gray-100'
                            )}
                          >
                            {/* 等级头部 */}
                            <div className={clsx(
                              'px-4 py-2 flex items-center justify-between',
                              config.bgColor
                            )}>
                              <div className="flex items-center gap-2">
                                <span className={clsx('font-semibold', config.color)}>
                                  {grade}级: {config.label}
                                </span>
                                {isRecommended && (
                                  <span className="text-xs px-1.5 py-0.5 bg-primary-600 text-white rounded-full">
                                    推荐
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-gray-500">
                                {config.description}
                              </span>
                            </div>

                            {/* 动作列表 */}
                            <div className="divide-y divide-gray-50">
                              {actions.map((action) => {
                                const isActionExpanded = expandedActionIds.has(action.id!)
                                const signals = action.expectedSignals || []
                                const observedCount = signals.filter(s => s.observed).length
                                const evidences = evidenceCache[hypothesis.hypothesisId] || []
                                
                                return (
                                <div key={action.id} className="p-4 bg-white relative">
                                  {/* 右上角状态标识 */}
                                  {action.status && action.status !== 'proposed' && (
                                    <div className="absolute top-3 right-3">
                                      <span className={clsx(
                                        'text-xs font-medium px-2 py-0.5 rounded-full',
                                        ACTION_STATUS_CONFIG[action.status]?.bgColor || 'bg-gray-100',
                                        ACTION_STATUS_CONFIG[action.status]?.color || 'text-gray-600'
                                      )}>
                                        {ACTION_STATUS_CONFIG[action.status]?.label || action.status}
                                      </span>
                                    </div>
                                  )}
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 pr-16">
                                      <p className={clsx(
                                        'text-gray-800',
                                        (action.status === 'rejected' || action.status === 'not_applicable') && 'line-through text-gray-400'
                                      )}>
                                        {action.content}
                                      </p>
                                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                                        <span className="flex items-center gap-1">
                                          <Clock className="w-3 h-3" />
                                          {action.timeCostDisplay || `${action.timeCostDays}天`}
                                        </span>
                                        <span className={clsx(
                                          'px-1.5 py-0.5 rounded',
                                          action.priority === 'P0' ? 'bg-red-100 text-red-700' :
                                          action.priority === 'P1' ? 'bg-amber-100 text-amber-700' :
                                          'bg-gray-100 text-gray-600'
                                        )}>
                                          {action.priority}
                                        </span>
                                        {/* TASK-H8: 信号追踪按钮 */}
                                        {signals.length > 0 && (
                                          <button
                                            onClick={() => {
                                              toggleActionExpand(action.id!)
                                              // 预加载证据
                                              if (!isActionExpanded && onFetchEvidences) {
                                                fetchEvidencesForHypothesis(hypothesis.hypothesisId)
                                              }
                                            }}
                                            className={clsx(
                                              'flex items-center gap-1 px-2 py-0.5 rounded transition-colors',
                                              isActionExpanded 
                                                ? 'bg-primary-100 text-primary-700' 
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            )}
                                          >
                                            <Eye className="w-3 h-3" />
                                            <span>信号 {observedCount}/{signals.length}</span>
                                            {isActionExpanded ? (
                                              <ChevronDown className="w-3 h-3" />
                                            ) : (
                                              <ChevronRight className="w-3 h-3" />
                                            )}
                                          </button>
                                        )}
                                      </div>
                                      {action.warning && (
                                        <p className="mt-2 text-xs text-amber-600">
                                          ⚠️ {action.warning}
                                        </p>
                                      )}
                                      
                                      {/* TASK-H8: 信号追踪展开区域 */}
                                      <AnimatePresence>
                                        {isActionExpanded && signals.length > 0 && (
                                          <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="mt-3 overflow-hidden"
                                          >
                                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                              {/* 信号追踪头部 */}
                                              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                                                <span className="text-xs font-medium text-gray-700">
                                                  信号追踪进度
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                  {observedCount}/{signals.length} 已观测
                                                </span>
                                              </div>
                                              
                                              {/* 信号列表 */}
                                              <div className="divide-y divide-gray-100">
                                                {signals.map((signal) => {
                                                  const signalId = signal.id || `${action.id}-${signal.description}`
                                                  const isSignalExpanded = expandedSignalIds.has(signalId)
                                                  const isLoading = loadingSignals.has(signalId)
                                                  
                                                  return (
                                                    <div key={signalId} className="bg-white">
                                                      {/* 信号头部行 */}
                                                      <div
                                                        onClick={() => !signal.observed && toggleSignalExpand(signalId)}
                                                        className={clsx(
                                                          'px-3 py-2 flex items-center gap-2 text-xs',
                                                          !signal.observed && 'cursor-pointer hover:bg-gray-50'
                                                        )}
                                                      >
                                                        {/* 展开/收起图标 */}
                                                        {signal.observed ? (
                                                          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                        ) : isSignalExpanded ? (
                                                          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                        ) : (
                                                          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                        )}
                                                        
                                                        {/* 信号强度标签 */}
                                                        <span className={clsx(
                                                          'px-1.5 py-0.5 rounded text-xs flex-shrink-0',
                                                          signal.strength === 'strong' ? 'bg-green-100 text-green-700' :
                                                          signal.strength === 'medium' ? 'bg-amber-100 text-amber-700' :
                                                          'bg-gray-100 text-gray-600'
                                                        )}>
                                                          {signal.strength === 'strong' ? '强' :
                                                           signal.strength === 'medium' ? '中' : '弱'}
                                                        </span>
                                                        
                                                        {/* 信号描述 */}
                                                        <span className={clsx(
                                                          'flex-1',
                                                          signal.observed ? 'text-gray-500 line-through' : 'text-gray-700'
                                                        )}>
                                                          {signal.description}
                                                        </span>
                                                        
                                                        {/* 已观测：显示取消按钮 */}
                                                        {signal.observed && onUnobserveSignal && (
                                                          <button
                                                            onClick={(e) => {
                                                              e.stopPropagation()
                                                              handleUnobserveSignal(signalId)
                                                            }}
                                                            disabled={isLoading}
                                                            className="flex items-center gap-1 px-2 py-0.5 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                            title="取消观测（不会回滚假设状态）"
                                                          >
                                                            {isLoading ? (
                                                              <Loader2 className="w-3 h-3 animate-spin" />
                                                            ) : (
                                                              <>
                                                                <EyeOff className="w-3 h-3" />
                                                                取消
                                                              </>
                                                            )}
                                                          </button>
                                                        )}
                                                      </div>
                                                      
                                                      {/* 信号展开区域：备注、证据选择、标记按钮 */}
                                                      <AnimatePresence>
                                                        {isSignalExpanded && !signal.observed && (
                                                          <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: 'auto', opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            transition={{ duration: 0.15 }}
                                                            className="overflow-hidden"
                                                          >
                                                            <div className="px-3 pb-3 pt-1 space-y-2 bg-gray-50/50">
                                                              {/* 证据选择器 */}
                                                              {onFetchEvidences && (
                                                                <div className="flex items-center gap-2">
                                                                  <Link2 className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                                  <select
                                                                    value={selectedEvidences[signalId] || ''}
                                                                    onChange={(e) => setSelectedEvidences(prev => ({
                                                                      ...prev,
                                                                      [signalId]: e.target.value
                                                                    }))}
                                                                    disabled={loadingEvidences.has(hypothesis.hypothesisId)}
                                                                    className="flex-1 text-xs px-2 py-1 border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                                                                  >
                                                                    <option value="">不关联证据（可选）</option>
                                                                    {evidences.map((ev) => (
                                                                      <option key={ev.id} value={ev.id}>
                                                                        [{ev.sourceType}] {ev.summary?.substring(0, 50)}...
                                                                      </option>
                                                                    ))}
                                                                  </select>
                                                                </div>
                                                              )}
                                                              
                                                              {/* 备注输入 */}
                                                              <input
                                                                type="text"
                                                                placeholder="添加观测备注（可选）"
                                                                value={signalNotes[signalId] || ''}
                                                                onChange={(e) => setSignalNotes(prev => ({
                                                                  ...prev,
                                                                  [signalId]: e.target.value
                                                                }))}
                                                                className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                                                              />
                                                              
                                                              {/* 标记按钮 */}
                                                              <button
                                                                onClick={() => handleObserveSignal(signalId)}
                                                                disabled={isLoading}
                                                                className="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded transition-colors disabled:opacity-50"
                                                              >
                                                                {isLoading ? (
                                                                  <Loader2 className="w-3 h-3 animate-spin" />
                                                                ) : (
                                                                  <>
                                                                    <Check className="w-3 h-3" />
                                                                    标记为已观测
                                                                  </>
                                                                )}
                                                              </button>
                                                            </div>
                                                          </motion.div>
                                                        )}
                                                      </AnimatePresence>
                                                    </div>
                                                  )
                                                })}
                                              </div>
                                            </div>
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  </div>

                                  {/* 操作按钮 */}
                                  {(() => {
                                    // 获取有效状态：undefined/null 默认为 'proposed'
                                    const effectiveStatus = action.status || 'proposed'
                                    const isProposed = effectiveStatus === 'proposed'
                                    const canOperate = ['proposed', 'accepted'].includes(effectiveStatus)
                                    const artifactRef = (action as { artifactRef?: unknown }).artifactRef
                                    
                                    return (
                                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                                    {/* 复制按钮 - 始终可用 */}
                                    <button
                                      onClick={() => handleCopy(action)}
                                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                    >
                                      {copiedId === action.id ? (
                                        <>
                                          <Check className="w-3 h-3 text-green-500" />
                                          已复制
                                        </>
                                      ) : (
                                        <>
                                          <Copy className="w-3 h-3" />
                                          复制
                                        </>
                                      )}
                                    </button>
                                    
                                    {/* 采纳按钮 - 仅 proposed 状态可用 */}
                                    <button
                                      onClick={() => handleOpenAcceptModal(action)}
                                      disabled={actionLoadingIds.has(action.id!) || !isProposed}
                                      className={clsx(
                                        'flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors',
                                        isProposed
                                          ? 'text-green-700 bg-green-100 hover:bg-green-200'
                                          : 'text-gray-400 bg-gray-100 cursor-not-allowed',
                                        'disabled:opacity-50'
                                      )}
                                      title={!isProposed ? `当前状态：${ACTION_STATUS_CONFIG[effectiveStatus]?.label || effectiveStatus}` : ''}
                                    >
                                      <Check className="w-3 h-3" />
                                      采纳
                                    </button>
                                    
                                    {/* 完成按钮 - proposed 或 accepted 状态可用 */}
                                    <button
                                      onClick={() => handleCompleteAction(action)}
                                      disabled={actionLoadingIds.has(action.id!) || !canOperate}
                                      className={clsx(
                                        'flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors',
                                        canOperate
                                          ? 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                                          : 'text-gray-400 bg-gray-100 cursor-not-allowed',
                                        'disabled:opacity-50'
                                      )}
                                      title={!canOperate ? `当前状态：${ACTION_STATUS_CONFIG[effectiveStatus]?.label || effectiveStatus}` : ''}
                                    >
                                      {actionLoadingIds.has(action.id!) ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <Shield className="w-3 h-3" />
                                      )}
                                      完成
                                    </button>
                                    
                                    {/* 拒绝按钮 - 仅 proposed 或 accepted 状态可用 */}
                                    <button
                                      onClick={() => handleOpenRejectModal(action)}
                                      disabled={actionLoadingIds.has(action.id!) || !canOperate}
                                      className={clsx(
                                        'flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors',
                                        canOperate
                                          ? 'text-red-700 bg-red-100 hover:bg-red-200'
                                          : 'text-gray-400 bg-gray-100 cursor-not-allowed',
                                        'disabled:opacity-50'
                                      )}
                                      title={!canOperate ? `当前状态：${ACTION_STATUS_CONFIG[effectiveStatus]?.label || effectiveStatus}` : ''}
                                    >
                                      <X className="w-3 h-3" />
                                      拒绝
                                    </button>
                                    
                                    {/* 不适用按钮 - 仅 proposed 或 accepted 状态可用 */}
                                    <button
                                      onClick={() => handleMarkNotApplicable(action.id!)}
                                      disabled={actionLoadingIds.has(action.id!) || !canOperate}
                                      className={clsx(
                                        'flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors',
                                        canOperate
                                          ? 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                                          : 'text-gray-400 bg-gray-100 cursor-not-allowed',
                                        'disabled:opacity-50'
                                      )}
                                      title={!canOperate ? `当前状态：${ACTION_STATUS_CONFIG[effectiveStatus]?.label || effectiveStatus}` : ''}
                                    >
                                      {actionLoadingIds.has(action.id!) ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : null}
                                      不适用
                                    </button>
                                    
                                    {/* 生成输出件按钮 - 始终可用 */}
                                    {isArtifactType(artifactRef) && dealId && (
                                      <button
                                        onClick={() => setArtifactModal({
                                          isOpen: true,
                                          artifactType: artifactRef,
                                          actionContent: action.content
                                        })}
                                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
                                      >
                                        <FileText className="w-3 h-3" />
                                        生成{ARTIFACT_TYPE_NAMES[artifactRef]}
                                      </button>
                                    )}
                                  </div>
                                    )
                                  })()}
                                </div>
                              )})}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )
        })}
      </div>

      {/* 底部提示 */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 text-center">
        <p className="text-xs text-gray-500">
          💡 动作建议基于当前证据生成，您可以根据实际情况选择或调整
        </p>
      </div>

      {/* 输出件生成弹窗 */}
      {dealId && artifactModal.artifactType && (
        <ArtifactQuickGenModal
          isOpen={artifactModal.isOpen}
          onClose={() => setArtifactModal({ isOpen: false, artifactType: null, actionContent: '' })}
          dealId={dealId}
          dealName={dealName || '商机'}
          artifactType={artifactModal.artifactType}
          actionContext={artifactModal.actionContent}
        />
      )}

      {/* ============================================ */}
      {/* 闭环弹窗 */}
      {/* ============================================ */}
      
      {/* 采纳确认弹窗 */}
      <ActionAcceptModal
        isOpen={acceptModal.isOpen}
        onClose={() => setAcceptModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={handleConfirmAccept}
        actionContent={acceptModal.actionContent}
        actionGrade={acceptModal.actionGrade}
        timeCostDisplay={acceptModal.timeCostDisplay}
      />
      
      {/* 信号确认弹窗 */}
      <SignalConfirmModal
        isOpen={signalModal.isOpen}
        onClose={handleCloseSignalModal}
        actionContent={signalModal.actionContent}
        pendingSignals={signalModal.pendingSignals}
        onObserveSignal={handleSignalModalObserve}
        onSkip={handleCloseSignalModal}
      />
      
      {/* 拒绝确认弹窗 */}
      <ActionRejectModal
        isOpen={rejectModal.isOpen}
        onClose={() => setRejectModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={handleConfirmReject}
        actionContent={rejectModal.actionContent}
        actionGrade={rejectModal.actionGrade}
      />
    </div>
  )
}

/**
 * 骨架屏
 */
export function ActionsByHypothesisPanelSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
        <div className="h-6 w-32 bg-gray-200 rounded" />
      </div>
      <div className="divide-y divide-gray-100">
        {[1, 2, 3].map((i) => (
          <div key={i} className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 bg-gray-200 rounded" />
                <div className="h-5 w-48 bg-gray-200 rounded" />
              </div>
              <div className="w-5 h-5 bg-gray-200 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
